// LearnCore Frontend - 家长管理API客户端

import axios, { AxiosInstance } from 'axios';
import { appConfig } from '../config/env';
import type {
  ApiResponse,
  ParentRegisterRequest,
  CreateChildRequest,
  UpdateChildRequest,
  ResetChildPasswordRequest,
  ChildLoginRequest
} from '../types';

class ParentApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: appConfig.apiBaseUrl,
      timeout: 30000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 请求拦截器
    this.client.interceptors.request.use(
      (config) => {
        console.log(`[ParentAPI] ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('[ParentAPI] Request error:', error);
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.client.interceptors.response.use(
      (response) => {
        console.log(`[ParentAPI] Response ${response.status}:`, response.data);
        return response;
      },
      (error) => {
        console.error('[ParentAPI] Response error:', error.response?.data || error.message);
        
        // 处理401错误 - 未授权
        if (error.response?.status === 401) {
          const currentPath = window.location.pathname;
          if (currentPath !== '/login' && currentPath !== '/register') {
            window.location.href = '/login';
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // 家长相关API
  async registerParent(request: ParentRegisterRequest): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>('/parent/register', request);
    return response.data;
  }

  async getParentProfile(): Promise<ApiResponse> {
    const response = await this.client.get<ApiResponse>('/parent/profile');
    return response.data;
  }

  async updateParentProfile(request: { display_name: string }): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>('/parent/profile', request);
    return response.data;
  }

  // 孩子账户管理API
  async createChild(request: CreateChildRequest): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>('/parent/children', request);
    return response.data;
  }

  async getChildren(): Promise<ApiResponse> {
    const response = await this.client.get<ApiResponse>('/parent/children');
    return response.data;
  }

  async getChild(childId: string): Promise<ApiResponse> {
    const response = await this.client.get<ApiResponse>(`/parent/children/${childId}`);
    return response.data;
  }

  async updateChild(childId: string, request: UpdateChildRequest): Promise<ApiResponse> {
    const response = await this.client.put<ApiResponse>(`/parent/children/${childId}`, request);
    return response.data;
  }

  async resetChildPassword(childId: string, request: ResetChildPasswordRequest): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>(`/parent/children/${childId}/reset-password`, request);
    return response.data;
  }

  async deleteChild(childId: string): Promise<ApiResponse> {
    const response = await this.client.delete<ApiResponse>(`/parent/children/${childId}`);
    return response.data;
  }

  // 家庭管理API
  async getFamilyOverview(): Promise<ApiResponse> {
    const response = await this.client.get<ApiResponse>('/parent/family/overview');
    return response.data;
  }

  async getPermissionConfig(): Promise<ApiResponse> {
    const response = await this.client.get<ApiResponse>('/parent/family/permissions');
    return response.data;
  }

  // 孩子登录API
  async childLogin(request: ChildLoginRequest): Promise<ApiResponse> {
    const response = await this.client.post<ApiResponse>('/auth/child/login', request);
    return response.data;
  }
}

// 创建并导出单例实例
export const parentApiClient = new ParentApiClient();

// 导出便捷的API对象
export const parentApi = {
  registerParent: (request: ParentRegisterRequest) => parentApiClient.registerParent(request),
  getParentProfile: () => parentApiClient.getParentProfile(),
  updateParentProfile: (request: { display_name: string }) => parentApiClient.updateParentProfile(request),
  createChild: (request: CreateChildRequest) => parentApiClient.createChild(request),
  getChildren: () => parentApiClient.getChildren(),
  getChild: (childId: string) => parentApiClient.getChild(childId),
  updateChild: (childId: string, request: UpdateChildRequest) => parentApiClient.updateChild(childId, request),
  resetChildPassword: (childId: string, request: ResetChildPasswordRequest) => parentApiClient.resetChildPassword(childId, request),
  deleteChild: (childId: string) => parentApiClient.deleteChild(childId),
  getFamilyOverview: () => parentApiClient.getFamilyOverview(),
  getPermissionConfig: () => parentApiClient.getPermissionConfig(),
  childLogin: (request: ChildLoginRequest) => parentApiClient.childLogin(request),
};